import * as api from '../../api';
import * as favoriteActions from './actions';
import { message } from '@myua/kit';
import { getAssetsFilter, getPermissions } from '../app/selectors';
import { Asset, LoadFavoritesReturn } from '../../api';
import { EMPTY_TOTAL, LoadingMode, newScrollHash } from '../finder/model';
import { getData, getInProgress, getHasNextPage, getScrollHash } from './selectors';
import { getSortingFieldKey, getSortingOrder } from '../sorting/selectors';
import { getSearchApiParams } from '../search/selectors';
import { getAllInProgress } from '../pack/selectors';
import { loadWithPagination } from '../finder/listeners';
import { takeOnce, takeLatest, takeEvery } from '../../listenerMiddleware';
import * as appActions from '../app/actions';
import { Permission } from '../../../general/model';

export const favoriteListeners = () => {
  init();
  watchLoadFavoritesRequested();
  watchAssetToggled();
  watchClearFavoritesClicked();
};

function init() {
  takeOnce(appActions.initialized, (action, { getState, dispatch }) => {
    // Надо загрузить избранное, не дожидаясь пока юзер перейдет в режим просмотра избранного, т.к.
    // эта информация нужна и в обычном режиме, чтобы отображать иконки добавления/удаления ассета.
    const permissions = getPermissions(getState());
    if (permissions[Permission['fs.assetlab_favorites']]) {
      dispatch(favoriteActions.loadFavoritesRequested({ mode: LoadingMode.refresh }));
    }
  });
}

function watchLoadFavoritesRequested() {
  takeLatest(favoriteActions.loadFavoritesRequested, async ({ payload }, listenerApi) => {
    const { getState } = listenerApi;
    const { mode } = payload;

    const errorState: LoadFavoritesReturn = { assets: [], total: EMPTY_TOTAL, asset_ids: [] };
    const sortingOrder = getSortingOrder(getState());
    const sortingFieldKey = getSortingFieldKey(getState());
    const searchApiParams = getSearchApiParams(getState());

    await loadWithPagination(
      {
        mode,
        getData,
        getScrollHash,
        setLoading: favoriteActions.setLoading,
        assetsChanged: favoriteActions.assetsChanged,
        errorMessage: 'Favorites have not been loaded',
        errorState,
        onRequest: (offset) =>
          api.loadFavorites({
            orderBy: sortingFieldKey,
            ordering: sortingOrder,
            ...searchApiParams,
            offset,
          }),
      },
      listenerApi,
    );
  });
}

function watchAssetToggled() {
  takeEvery(favoriteActions.assetsToggled, async ({ payload }, { getState, dispatch }) => {
    const { assets, add, selectAll } = payload;

    // Если какой-то из затронутых ассетов уже в обработке, то выходим из саги.
    // UI, в принципе, не дает возникнуть такой ситуации.
    const allInProgress = getAllInProgress(getState());
    const inProgress = getInProgress(getState());
    if (allInProgress || assets?.some(({ id }) => inProgress[id])) {
      return;
    }

    const ids = (assets ?? []).map(({ id }) => id);

    let result;

    if (selectAll) {
      dispatch(favoriteActions.setAllInProgress({ allInProgress: true }));
      const filter = getAssetsFilter(getState());
      if (add) {
        result = await api.addToFavorites({ filter });
      } else {
        result = await api.deleteFromFavorites({ filter });
      }
      dispatch(favoriteActions.setAllInProgress({ allInProgress: false }));
    } else {
      dispatch(favoriteActions.setInProgress({ ids, inProgress: true }));
      if (add) {
        result = await api.addToFavorites({ ids });
      } else {
        result = await api.deleteFromFavorites({ ids });
      }
      dispatch(favoriteActions.setInProgress({ ids, inProgress: false }));
    }

    if (!result.success) {
      return;
    }

    const assetsInFavorite: Partial<Record<Asset['id'], boolean>> = {};
    result.data.response.asset_ids.forEach((id) => {
      assetsInFavorite[id] = true;
    });
    const data = getData(getState());
    const newAssets = add ? data.assets : data.assets.filter(({ id }) => assetsInFavorite[id]);
    const hasNextPage = getHasNextPage(getState());
    const scrollHash = getScrollHash(getState());
    dispatch(
      favoriteActions.assetsChanged({
        data: {
          assets: newAssets,
          total: result.data.response.total,
          asset_ids: result.data.response.asset_ids,
        },
        hasNextPage,
        scrollHash,
      }),
    );
  });
}

function watchClearFavoritesClicked() {
  takeLatest(favoriteActions.clearFavoritesClicked, async (action, { dispatch, signal }) => {
    const messageKey = 'favorites.delete';
    void message.loading({ key: messageKey, content: 'Deleting...' });
    const { success } = await api.clearFavorites();
    if (signal.aborted) {
      return;
    }
    if (success) {
      dispatch(
        favoriteActions.assetsChanged({
          data: { assets: [], total: EMPTY_TOTAL, asset_ids: [] },
          hasNextPage: false,
          scrollHash: newScrollHash(),
        }),
      );
      void message.success({ key: messageKey, content: 'Favorites have been cleared' });
    } else {
      void message.error({ key: messageKey, content: 'Favorites have not been cleared' });
    }
  });
}
